import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { 
  ArrowLeft, Plus, Trash2, GripVertical, Lightbulb, HelpCircle, 
  Heart, Leaf, Star, FileText, X, Save, Maximize2, Minimize2,
  ZoomIn, ZoomOut, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Item type configurations
const ITEM_TYPES = {
  idea: { icon: Lightbulb, label: 'Idea', defaultColor: '#fbbf24' },
  question: { icon: HelpCircle, label: 'Question', defaultColor: '#60a5fa' },
  feeling: { icon: Heart, label: 'Feeling', defaultColor: '#f87171' },
  need: { icon: Leaf, label: 'Need', defaultColor: '#34d399' },
  experiment: { icon: Star, label: 'Experiment', defaultColor: '#fbbf24' },
  note: { icon: FileText, label: 'Note', defaultColor: '#a78bfa' },
};

// Draggable sticky note component
function SketchNote({ item, onUpdate, onDelete, isSelected, onSelect }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(item.title);
  const [editContent, setEditContent] = useState(item.content || '');
  const [position, setPosition] = useState({ x: item.x || 100, y: item.y || 100 });
  const dragRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });

  const ItemIcon = ITEM_TYPES[item.type]?.icon || FileText;

  const handleMouseDown = (e) => {
    if (isEditing) return;
    setIsDragging(true);
    onSelect(item._id);
    startPosRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const newX = e.clientX - startPosRef.current.x;
    const newY = e.clientY - startPosRef.current.y;
    setPosition({ x: Math.max(0, newX), y: Math.max(0, newY) });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      onUpdate(item._id, { x: position.x, y: position.y });
    }
  }, [isDragging, position, item._id, onUpdate]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleSaveEdit = () => {
    onUpdate(item._id, { title: editTitle, content: editContent });
    setIsEditing(false);
  };

  return (
    <div
      ref={dragRef}
      className={`absolute select-none transition-shadow ${isDragging ? 'z-50 cursor-grabbing' : 'cursor-grab'} ${isSelected ? 'ring-2 ring-white/50' : ''}`}
      style={{
        left: position.x,
        top: position.y,
        transform: isDragging ? 'scale(1.05) rotate(-1deg)' : 'rotate(0deg)',
      }}
      onMouseDown={handleMouseDown}
    >
      <div 
        className="w-52 min-h-[120px] rounded-lg shadow-xl backdrop-blur-sm border border-white/20 overflow-hidden"
        style={{ 
          backgroundColor: `${item.color}dd`,
          boxShadow: isDragging ? '0 20px 40px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.3)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-black/10">
          <div className="flex items-center gap-2">
            <ItemIcon className="w-4 h-4 text-black/60" />
            <span className="text-xs font-medium text-black/60 uppercase tracking-wide">
              {ITEM_TYPES[item.type]?.label || 'Note'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {!isEditing && (
              <button 
                onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                className="p-1 hover:bg-black/10 rounded"
              >
                <FileText className="w-3 h-3 text-black/50" />
              </button>
            )}
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete(item._id); }}
              className="p-1 hover:bg-black/10 rounded"
            >
              <X className="w-3 h-3 text-black/50" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-3 py-2">
          {isEditing ? (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="bg-white/50 border-black/20 text-black text-sm font-medium"
                placeholder="Title..."
              />
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="bg-white/50 border-black/20 text-black text-xs min-h-[60px]"
                placeholder="Details..."
              />
              <div className="flex gap-1">
                <Button size="sm" onClick={handleSaveEdit} className="text-xs bg-black/20 hover:bg-black/30 text-black">
                  <Save className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="text-xs text-black/60">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h4 className="font-semibold text-black/90 text-sm mb-1">{item.title}</h4>
              {item.content && (
                <p className="text-xs text-black/70 leading-relaxed">{item.content}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ElchmESketchboard() {
  const navigate = useNavigate();
  const { boardId } = useParams();
  const canvasRef = useRef(null);
  
  const [board, setBoard] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [newItemType, setNewItemType] = useState('idea');
  const [newItemTitle, setNewItemTitle] = useState('');
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  // Load or create board
  useEffect(() => {
    if (boardId) {
      fetchBoard();
      fetchItems();
    } else {
      // Create a new board
      createNewBoard();
    }
  }, [boardId]);

  const createNewBoard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/elchme/sketchboards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'New Sketchboard' })
      });
      const newBoard = await response.json();
      navigate(`/elchme/sketchboard/${newBoard._id}`, { replace: true });
    } catch (error) {
      toast.error('Failed to create sketchboard');
      setLoading(false);
    }
  };

  const fetchBoard = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/elchme/sketchboards/${boardId}`);
      if (response.ok) {
        const data = await response.json();
        setBoard(data);
      }
    } catch (error) {
      console.error('Failed to fetch board:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/elchme/sketchboards/${boardId}/items`);
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemTitle.trim()) return;

    // Calculate position in center of visible area
    const canvas = canvasRef.current;
    const centerX = (canvas?.clientWidth / 2 || 400) / zoom - panOffset.x + Math.random() * 100 - 50;
    const centerY = (canvas?.clientHeight / 2 || 300) / zoom - panOffset.y + Math.random() * 100 - 50;

    try {
      const response = await fetch(`${BACKEND_URL}/api/elchme/sketchboards/${boardId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boardId,
          type: newItemType,
          title: newItemTitle,
          color: ITEM_TYPES[newItemType]?.defaultColor || '#fbbf24',
          x: centerX,
          y: centerY
        })
      });
      const newItem = await response.json();
      setItems([...items, newItem]);
      setNewItemTitle('');
      setShowAddMenu(false);
      toast.success('Added to sketchboard');
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/elchme/items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const updatedItem = await response.json();
      setItems(items.map(item => item._id === itemId ? updatedItem : item));
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await fetch(`${BACKEND_URL}/api/elchme/items/${itemId}`, { method: 'DELETE' });
      setItems(items.filter(item => item._id !== itemId));
      toast.success('Removed from sketchboard');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));
  const handleResetView = () => { setZoom(1); setPanOffset({ x: 0, y: 0 }); };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading sketchboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-sm border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/elchme')} 
              variant="ghost" 
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              ElchmE
            </Button>
            <div>
              <h1 className="text-lg font-light text-white">
                <span className="text-amber-400">Sketchboard</span>
              </h1>
              <p className="text-xs text-slate-500">Brainstorm · Explore · Connect</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-slate-800/50 rounded-lg px-2 py-1">
              <button onClick={handleZoomOut} className="p-1 text-slate-400 hover:text-white">
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={handleZoomIn} className="p-1 text-slate-400 hover:text-white">
                <ZoomIn className="w-4 h-4" />
              </button>
              <button onClick={handleResetView} className="p-1 text-slate-400 hover:text-white ml-1">
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>

            {/* Add Button */}
            <Button 
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="bg-amber-600 hover:bg-amber-500 text-slate-900"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </header>

      {/* Add Menu Popup */}
      {showAddMenu && (
        <div className="fixed top-16 right-4 z-50 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4 w-80">
          <h3 className="text-white font-medium mb-3">Add to Sketchboard</h3>
          <form onSubmit={handleAddItem} className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(ITEM_TYPES).map(([type, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setNewItemType(type)}
                    className={`p-2 rounded-lg border transition-all ${
                      newItemType === type 
                        ? 'border-amber-500 bg-amber-500/20' 
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: config.defaultColor }} />
                    <span className="text-xs text-slate-300">{config.label}</span>
                  </button>
                );
              })}
            </div>
            <Input
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.target.value)}
              placeholder="What's on your mind?"
              className="bg-slate-900 border-slate-600 text-white"
              autoFocus
            />
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-500 text-slate-900">
                Add
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowAddMenu(false)} className="text-slate-400">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Canvas with Graph Paper Background */}
      <div 
        ref={canvasRef}
        className="absolute inset-0 pt-16 overflow-hidden"
        style={{
          backgroundImage: `
            linear-gradient(rgba(100, 116, 139, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(100, 116, 139, 0.1) 1px, transparent 1px),
            linear-gradient(rgba(100, 116, 139, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(100, 116, 139, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: `${100 * zoom}px ${100 * zoom}px, ${100 * zoom}px ${100 * zoom}px, ${20 * zoom}px ${20 * zoom}px, ${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${panOffset.x}px ${panOffset.y}px`
        }}
        onClick={() => setSelectedItem(null)}
      >
        {/* Sketchboard Items */}
        <div 
          className="relative w-full h-full"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left'
          }}
        >
          {items.map(item => (
            <SketchNote
              key={item._id}
              item={item}
              onUpdate={handleUpdateItem}
              onDelete={handleDeleteItem}
              isSelected={selectedItem === item._id}
              onSelect={setSelectedItem}
            />
          ))}

          {/* Empty State */}
          {items.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800/50 flex items-center justify-center">
                  <Lightbulb className="w-8 h-8 text-amber-500/50" />
                </div>
                <p className="text-slate-500 text-lg mb-2">Your canvas awaits</p>
                <p className="text-slate-600 text-sm">Click "Add" to start brainstorming</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Toolbar */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
        <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-700 rounded-full px-4 py-2 flex items-center gap-4">
          <span className="text-xs text-slate-500">{items.length} items</span>
          <div className="w-px h-4 bg-slate-700"></div>
          <span className="text-xs text-slate-400">Drag notes to arrange</span>
        </div>
      </div>
    </div>
  );
}
